import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChecks, ChevronRight, CheckCircle2, Clock, RotateCcw, SkipForward } from "lucide-react";
import { listCoupleSessions, listPatientSessions, listSessionAgreements } from "../../application/sessions";
import type { Session, SessionAgreement } from "../../../session";

interface Props {
  patientId: string;
  onViewAll: () => void;
  isCouple?: boolean;
}

interface StatusCounts {
  pending: number;
  in_progress: number;
  completed: number;
  skipped: number;
}

const STATUS_CONFIG = [
  { key: "completed",   label: "portal.agreement.status.completed",   icon: <CheckCircle2 className="w-3.5 h-3.5" />, bar: "bg-green-400",  badge: "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  { key: "in_progress", label: "portal.agreement.status.in_progress", icon: <RotateCcw className="w-3.5 h-3.5" />,   bar: "bg-accent",   badge: "bg-accent-tint text-accent-text" },
  { key: "pending",     label: "portal.agreement.status.pending",     icon: <Clock className="w-3.5 h-3.5" />,        bar: "bg-gray-300 dark:bg-gray-600",   badge: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" },
  { key: "skipped",     label: "portal.agreement.status.skipped",     icon: <SkipForward className="w-3.5 h-3.5" />, bar: "bg-red-300",    badge: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400" },
] as const;

export default function PatientAgreementsSummaryCard({ patientId, onViewAll, isCouple = false }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<StatusCounts>({ pending: 0, in_progress: 0, completed: 0, skipped: 0 });

  useEffect(() => {
    (isCouple
      ? listCoupleSessions(patientId, { limit: 50 })
      : listPatientSessions(patientId, { limit: 50 })
    )
      .then((data) => {
        const list: Session[] = Array.isArray(data) ? data : (data as { sessions?: Session[] }).sessions ?? [];
        const completed = list.filter((s) => s.status === "completed");
        return Promise.all(
          completed.map((s) =>
            listSessionAgreements(s.id).then((agreements) =>
              agreements.filter((a: SessionAgreement) => a.state === "published")
            )
          )
        );
      })
      .then((results) => {
        const all = results.flat();
        const c: StatusCounts = { pending: 0, in_progress: 0, completed: 0, skipped: 0 };
        all.forEach((a) => {
          const s = (a.status ?? "pending") as keyof StatusCounts;
          if (s in c) c[s]++;
        });
        setTotal(all.length);
        setCounts(c);
      })
      .finally(() => setLoading(false));
  }, [patientId, isCouple]);

  const completedPct = total > 0 ? Math.round((counts.completed / total) * 100) : 0;

  return (
    <div className="glass-effect glass-border border rounded-2xl p-5 shadow-theme-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
            <ListChecks className="w-4 h-4 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            {t("patient.patientAgreements.title")}
          </h3>
        </div>
        <button
          onClick={onViewAll}
          className="flex items-center gap-0.5 text-xs font-medium text-brand-600 dark:text-brand-400 hover:gap-1 transition-all"
        >
          {t("patient.patientAgreements.viewAll")}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          <div className="h-4 bg-gray-100 dark:bg-white/5 rounded-full animate-pulse" />
          <div className="h-8 bg-gray-100 dark:bg-white/5 rounded-xl animate-pulse" />
        </div>
      ) : total === 0 ? (
        <p className="text-sm text-content-tertiary italic">{t("patient.patientAgreements.noAgreementsYet")}</p>
      ) : (
        <div className="space-y-4">
          {/* Progress bar */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-content-tertiary">
                {t("patient.patientAgreements.summaryTotal", { count: total })}
              </span>
              <span className="text-xs font-bold text-green-600 dark:text-green-400">
                {completedPct}%
              </span>
            </div>
            {/* Segmented progress bar */}
            <div className="flex h-2 w-full rounded-full overflow-hidden gap-px bg-gray-100 dark:bg-gray-800">
              {STATUS_CONFIG.map(({ key, bar }) =>
                counts[key as keyof StatusCounts] > 0 ? (
                  <div
                    key={key}
                    className={`${bar} transition-all duration-500`}
                    style={{ width: `${(counts[key as keyof StatusCounts] / total) * 100}%` }}
                  />
                ) : null
              )}
            </div>
          </div>

          {/* Status breakdown */}
          <div className="grid grid-cols-2 gap-2">
            {STATUS_CONFIG.filter(({ key }) => counts[key as keyof StatusCounts] > 0).map(({ key, label, icon, badge }) => (
              <div
                key={key}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl ${badge}`}
              >
                <span className="shrink-0">{icon}</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold leading-none">{counts[key as keyof StatusCounts]}</p>
                  <p className="text-xs opacity-75 mt-0.5 truncate">{t(label)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
